import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import { Divider, FormControl, ListItemIcon, Menu, MenuItem, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartSimple, faCloud, faHouse, faImage, faKey, faLaptopCode, faRightFromBracket, faTable, faUser } from "@fortawesome/free-solid-svg-icons";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { clearAuthData, selectDatasetsList, selectVersion } from "../../../../store/authSlice";
import {
	changeDataset,
	clearChangeDatasetStatus,
	clearChangePasswordStatus,
	selectChangeDatasetStatus,
	selectChangePasswordStatus,
	selectCurrentUserInfo,
	updateCurrentUserDataset,
} from "../../../../store/userSlice";
import { selectWallpaper } from "../../../../store/langSlice";
import useAccessRight from "../../../../hooks/useAccessRight";
import useTranslate from "../../../../hooks/useTranslate";
import { LOGIN, SES } from "../../../../constants/routes";
import { USER } from "../../../../constants/accessRights";
import { ACCESS_TOKEN, BACKGROUND_IMAGE, CLOUD_DATA_PRESENTATION, REFRESH_TOKEN } from "../../../../constants/cookieNames";
import { backgroundColor, colorPrimary, colorRed, colorSecondary } from "../../../../constants/colors";
import { RequestStatus, ResponseStatus } from "@/types/statusTypes";
import CustomTextField from "@/components/MUI/CustomTextField/CustomTextField";
import CustomMenuItem from "@/components/MUI/CustomMenuItem/CustomMenuItem";
import ControlPanelButton from "../../../../ui/ControlPanelButton/ControlPanelButton";
import FormChangePassword from "../../../Forms/FormChangePassword/FormChangePassword";
import Notification from "../../../MUI/Notification/Notification";
import { IUserMenuProps } from "./UserMenu.props";
import styles from "./UserMenu.module.scss";
import { useCrossTabReload } from "@/hooks";
import { Hint } from "@/HOC";

const UserMenu = ({ setShowPage, hint = "" }: IUserMenuProps): JSX.Element => {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null); // якорь для меню пользователя
	const [showModalChangePass, setShowModalChangePass] = useState<boolean>(false); // показ формы для изменения пароля
	const [showNotificationChangePassword, setShowNotificationChangePassword] = useState<boolean>(false); // показ уведомления изменения пароля
	const [showNotificationChangeDataset, setShowNotificationChangeDataset] = useState<boolean>(false); // показ уведомления изменения набора данных

	const dispatch = useAppDispatch();
	const currentUserInfo = useAppSelector(selectCurrentUserInfo); // store - информация о текущем пользователе
	const wallpaper = useAppSelector(selectWallpaper); // store - обои
	const version = useAppSelector(selectVersion); // store - информация о версии системы
	const datasetsList = useAppSelector(selectDatasetsList); // store - список наборов данных
	const changeDatasetStatus = useAppSelector(selectChangeDatasetStatus); // store - статус изменения набора данных пользователя
	const { notifyDataChanged } = useCrossTabReload();
	const isLocalDatasetChange = useRef(false);

	const [selectDataset, setSelectDataset] = useState<string>(currentUserInfo.dataset || "");

	const navigate = useNavigate();
	const location = useLocation();
	const [cookies, setCookie, removeCookies] = useCookies([ACCESS_TOKEN, REFRESH_TOKEN, BACKGROUND_IMAGE, CLOUD_DATA_PRESENTATION]);

	const isAccess = useAccessRight();
	const translate = useTranslate();

	// следим за статусом изменения датасета
	useEffect(() => {
		if (changeDatasetStatus.error === ResponseStatus.SUCCESS && changeDatasetStatus.status === RequestStatus.IDLE && changeDatasetStatus.message !== "") {
			dispatch(updateCurrentUserDataset(selectDataset));

			if (isLocalDatasetChange.current) {
				notifyDataChanged(); // текущая вкладка не перезагрузится
				isLocalDatasetChange.current = false;
			}
		}
	}, [changeDatasetStatus]);

	// обработчик изменения набора данных
	const datasetChangeHandler = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
		setSelectDataset(e.target.value);
		dispatch(changeDataset(e.target.value)); // меняем датасет
		setShowNotificationChangeDataset(true); // ставим уведомление
		setAnchorEl(null); // закрываем меню

		isLocalDatasetChange.current = true;
	};

	// обработчик открытия формы изменения пароля
	const changePasswordHandler = (): void => {
		setAnchorEl(null); // закрываем меню
		setShowModalChangePass(true); // открываем форму
	};

	// вкл/выкл фонового изображения
	const toggleBackgroundImage = (): void => {
		setCookie(BACKGROUND_IMAGE, cookies.backgroundImage === "false" ? "true" : "false", { path: "/", maxAge: 31_536_000 }); // устанавливаем cookie на год
	};

	// переключение облачного/табличного представления данных робота
	const toggleDataPresentation = (): void => {
		setCookie(CLOUD_DATA_PRESENTATION, cookies.cloudDataPresentation === "table" ? "column" : cookies.cloudDataPresentation === "column" ? "cloud" : "table", {
			path: "/",
			maxAge: 31_536_000,
		}); // устанавливаем cookie на год
	};

	// навигация домой
	const navigateToHome = (): void => {
		setAnchorEl(null); // закрываем меню
		setShowPage(false); // уводим страницу в фон
		setTimeout(() => {
			navigate("/");
		}, 500); // после скрытия страницы переходим через 500мс
	};

	// logout
	const logout = (): void => {
		setAnchorEl(null); // закрываем меню
		setShowPage(false); // уводим страницу в фон
		setTimeout(() => {
			removeCookies(ACCESS_TOKEN, { path: "/" });
			removeCookies(REFRESH_TOKEN, { path: "/" });
			dispatch(clearAuthData());
			navigate(LOGIN, { state: location.pathname });
		}, 500); // после скрытия страницы переходим через 500мс
	};

	return (
		<>
			<ControlPanelButton onClick={(e) => setAnchorEl(e.currentTarget)} hint={hint} disabled={false} title="button_userMenu" icon={faUser} disableDefaultIcon>
				{currentUserInfo.image ? (
					<span className={styles.imageWrapper}>
						<img src={currentUserInfo.image} alt="avatar" />
					</span>
				) : (
					<FontAwesomeIcon icon={faUser} size="xl" color={backgroundColor} />
				)}
			</ControlPanelButton>
			<Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
				<div>
					{/* имя пользователя */}
					<Typography color={colorSecondary} marginBottom={1} padding="0 10px">
						{currentUserInfo.fullName}
					</Typography>

					<Divider />

					{/* версия ups */}
					{version && "version" in version && (
						<>
							<Typography color={colorPrimary} textAlign="left" padding="6px 16px">
								<FontAwesomeIcon icon={faLaptopCode} size="lg" color={colorPrimary} style={{ marginRight: "11px" }} />
								{translate("title_version")}: {version.version}
							</Typography>
							<Divider />
						</>
					)}

					{/* набор данных */}
					{isAccess(USER.MODIFY) && (
						<FormControl fullWidth margin="dense" sx={{ paddingInline: "16px" }}>
							<CustomTextField
								select
								label={translate("select_defaultDataset")}
								value={selectDataset}
								onChange={datasetChangeHandler}
								disabled={changeDatasetStatus.status === RequestStatus.LOADING}
								sx={{ textAlign: "left" }}
							>
								{currentUserInfo.datasets.map((datasetId) => (
									<CustomMenuItem key={datasetId} value={datasetId}>
										{datasetsList.dictionary[datasetId] || `${datasetId} (${translate("title_notFound")})`}
									</CustomMenuItem>
								))}
							</CustomTextField>
						</FormControl>
					)}

					{/* смена пароля */}
					{isAccess(USER.PASSWORD) && (
						<MenuItem onClick={changePasswordHandler} sx={{ color: colorPrimary }}>
							<ListItemIcon>
								<FontAwesomeIcon icon={faKey} size="lg" color={colorPrimary} />
							</ListItemIcon>
							{translate("selectItem_changePassword")}
						</MenuItem>
					)}

					{/* вкл/выкл фона */}
					{wallpaper.dataUrl && (
						<MenuItem onClick={toggleBackgroundImage} sx={{ color: colorPrimary }}>
							<ListItemIcon>
								<FontAwesomeIcon icon={faImage} size="lg" color={colorPrimary} />
							</ListItemIcon>
							{translate(cookies.backgroundImage === "false" ? "selectItem_showBackground" : "selectItem_hideBackground")}
						</MenuItem>
					)}

					{/* переключение облачного/табличного представления данных робота */}
					{location.pathname.includes(SES) && (
						<Hint
							hint={cookies.cloudDataPresentation === "table" ? "hint_columnarView" : cookies.cloudDataPresentation === "column" ? "hint_cloudyView" : "hint_tableView"}
							position="left"
						>
							<MenuItem onClick={toggleDataPresentation} sx={{ color: colorPrimary }}>
								<ListItemIcon>
									<FontAwesomeIcon
										icon={cookies.cloudDataPresentation === "table" ? faChartSimple : cookies.cloudDataPresentation === "column" ? faCloud : faTable}
										size="lg"
										color={colorPrimary}
									/>
								</ListItemIcon>
								{translate(
									cookies.cloudDataPresentation === "table"
										? "selectItem_columnarView"
										: cookies.cloudDataPresentation === "column"
										? "selectItem_cloudyView"
										: "selectItem_tableView"
								)}
							</MenuItem>
						</Hint>
					)}

					{/* на главную */}
					<MenuItem onClick={navigateToHome} sx={{ color: colorPrimary }}>
						<ListItemIcon>
							<FontAwesomeIcon icon={faHouse} size="lg" color={colorPrimary} />
						</ListItemIcon>
						{translate("selectItem_toMainPage")}
					</MenuItem>

					{/* logout */}
					<MenuItem onClick={logout} sx={{ color: colorRed }}>
						<ListItemIcon>
							<FontAwesomeIcon icon={faRightFromBracket} size="lg" color={colorRed} />
						</ListItemIcon>
						{translate("selectItem_logout")}
					</MenuItem>
				</div>
			</Menu>
			{showModalChangePass && (
				<FormChangePassword showModal={showModalChangePass} setShowModal={setShowModalChangePass} setShowNotification={setShowNotificationChangePassword} />
			)}

			{/* уведомление об изменении пароля */}
			{showNotificationChangePassword && (
				<Notification
					showNotification={showNotificationChangePassword}
					setShowNotification={setShowNotificationChangePassword}
					selectDataResponse={selectChangePasswordStatus}
					clearDataResponse={clearChangePasswordStatus}
					titleFailed="noticeChange_failed"
					titleSuccess="noticeChange_success"
				/>
			)}

			{/* уведомление об изменении набора данных */}
			{showNotificationChangeDataset && (
				<Notification
					showNotification={showNotificationChangeDataset}
					setShowNotification={setShowNotificationChangeDataset}
					selectDataResponse={selectChangeDatasetStatus}
					clearDataResponse={clearChangeDatasetStatus}
					titleFailed="noticeChange_failed"
					titleSuccess="noticeChange_success"
				/>
			)}
		</>
	);
};

export default UserMenu;
